<template>
  <Head
    :canonical="route('frontend.index')"
  />

  <section class="bg-orange/90 pb-7 md:pb-0 mb-5 md:mb-12 border-t-4 border-gray-100/10">
    <div class="container grid grid-cols-1 lg:grid-cols-2 gap-12 py-12">
      <div>
        <h1 class="text-white text-4xl font-bold mb-5">
          {{ __('index.guest.hero.title') }}
        </h1>
        <ul class="text-white text-xl font-bold space-y-3 mb-8">
          <li class="flex items-center gap-2">
            <img
              src="/qassa/images/check.svg"
              alt=""
            >
            {{ __('index.guest.hero.point1') }}
          </li>
          <li class="flex items-center gap-2">
            <img
              src="/qassa/images/check.svg"
              alt=""
            >
            {{ __('index.guest.hero.point2') }}
          </li>
          <li class="flex items-center gap-2">
            <img
              src="/qassa/images/check.svg"
              alt=""
            >
            {{ __('index.guest.hero.point3') }}
          </li>
        </ul>

        <div class="flex flex-col xl:flex-row gap-3">
          <BlueButton :href="route('frontend.register')">
            {{ __('index.guest.hero.register-button') }}
          </BlueButton>

          <button
            v-if="!$page.props.auth.member"
            class="flex justify-center items-center text-white font-bold no-underline rounded-full w-full py-2 px-2 bg-transparent hover:bg-blue/10 focus:bg-blue-light/10 md:text-lg md:py-3"
            @click="$parent.$refs.loginModal.doOpenModal()"
          >
            {{ __('index.guest.hero.login-button') }}
          </button>
        </div>
      </div>

      <div class="flex flex-col justify-center items-center w-full">
        <video
          controls
          :poster="`/qassa/images/video-poster-${$page.props.language}.png`"
          class="w-full"
        >
          <source
            :src="`/qassa/videos/explain-${$page.props.language}.webm`"
            type="video/webm"
          >
          <source
            :src="`/qassa/videos/explain-${$page.props.language}.mp4`"
            type="video/mp4"
          >
        </video>
      </div>
    </div>

    <div
      v-if="famousWebshopsTop5.length > 0"
      class="container pb-8"
    >
      <WebshopSimpleSwiper
        :webshops="famousWebshopsTop5"
        block-type="logo_only"
        :disable-click="true"
      />
    </div>
  </section>

  <section class="bg-white md:bg-gray-100 pb-7 md:pb-0 mb-5 md:mb-12">
    <div class="container flex flex-col items-center">
      <h2 class="text-2xl font-bold mb-4">
        {{ __('index.guest.title') }}
      </h2>

      <p class="text-center mb-8">
        {{ __('content.how-it-works.content.text') }}
      </p>

      <div class="container mb-6">
        <Splide
          :options="options"
          :has-track="false"
        >
          <div class="relative">
            <div class="splide__arrows">
              <button class="splide__arrow splide__arrow--prev absolute z-10 -left-2 lg:-left-8 top-1/2 -translate-y-1/2 flex justify-center items-center h-8 w-8 bg-white shadow rounded-full cursor-pointer">
                <span class="material-symbols-outlined font-bold">
                  chevron_left
                </span>
              </button>
              <button class="splide__arrow splide__arrow--next absolute z-10 -right-2 lg:-right-8 top-1/2 -translate-y-1/2 flex justify-center items-center h-8 w-8 bg-white shadow rounded-full cursor-pointer">
                <span class="material-symbols-outlined font-bold pl-0.5">
                  chevron_right
                </span>
              </button>
            </div>

            <SplideTrack>
              <SplideSlide
                v-for="(slide, i) in $tm('content.how-it-works.slides')"
                :key="`slide-${i}`"
                class="flex flex-col justify-center items-center !my-0.5"
              >
                <div class="flex flex-col justify-center items-center text-center bg-white rounded shadow relative p-6 h-full w-full">
                  <img
                    :src="`/qassa/images/how-it-works-${i}.svg`"
                    class="mb-4"
                  >
                  {{ $rt(slide) }}
                </div>
              </SplideSlide>
            </SplideTrack>
          </div>
        </Splide>
      </div>

      <div class="flex flex-col items-center w-full">
        <div class="md:w-auto text-center">
          <BlueButton :href="route('frontend.register')">
            {{ __('index.guest.register') }}
          </BlueButton>

          <Link
            :href="route('frontend.how-it-works')"
            class="block mt-4 text-base"
          >
            {{ __('index.guest.how-it-works-link') }}
          </Link>
        </div>
      </div>
    </div>
  </section>

  <LastEarnedCashbackSwiper :latest-earned-cashbacks="latestEarnedCashbacks" />

  <section
    v-if="famousWebshops.length > 0"
    class="container mb-6 md:mb-12"
  >
    <div class="flex justify-center items-center">
      <h2 class="text-lg md:text-2xl font-bold mb-6">
        {{ __('index.guest.top-shops', {count: famousWebshopsLimited.length}) }}
      </h2>
    </div>

    <div class="grid gap-2.5 md:gap-8 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      <template
        v-for="webshop in famousWebshopsLimited"
        :key="`famous-webshop-${webshop.slug}`"
      >
        <WebshopBlock :webshop="webshop" />
      </template>
    </div>
  </section>
</template>

<script>
import {Splide, SplideSlide, SplideTrack} from '@splidejs/vue-splide';
import BlueButton from '../../Components/Buttons/Blue.vue';
import WebshopBlock from '../../Components/Webshop/Block.vue';
import LastEarnedCashbackSwiper from '../../Components/LastEarnedCashbackSwiper.vue';
import WebshopSimpleSwiper from '../../Components/WebshopSimpleSwiper.vue';

export default {
  components: {
    WebshopSimpleSwiper,
    Splide, SplideSlide, SplideTrack,
    WebshopBlock,
    BlueButton,
    LastEarnedCashbackSwiper,
  },

  props: {
    famousWebshops: {
      type: Object,
      required: true,
    },

    summedDonePayoutsAmount: {
      type: String,
      required: true,
    },

    startingBalance: {
      type: String,
      required: true,
    },

    tellAFriendBonus: {
      type: String,
      required: true,
    },

    emailClickValue: {
      type: String,
      required: true,
    },

    minimumPayoutValue: {
      type: String,
      required: true,
    },

    latestEarnedCashbacks: {
      type: Array,
      required: true,
    },
  },

  setup(props) {
    return {
      famousWebshopsTop5: props.famousWebshops.slice(0, 5),
      famousWebshopsLimited: props.famousWebshops.slice(0, 10),
      options: {
        gap: '1rem',
        autoHeight: true,
        pagination: false,
        padding: {left: 10, right: 2},
        rewind: true,
        mediaQuery: 'min',
        breakpoints: {
          640: {
            perPage: 1,
            arrows: true,
          },

          1024: {
            arrows: false,
            perPage: 4,
          },
        },
      },
    };
  },
};
</script>
